import CategoryBand from '@v2/shared/components/CategoryBand';
import Link from '@v2/shared/components/Link';
import SearchForm from '@v2/shared/components/SearchForm';
import useSearchAutoComplete from '@v2/shared/lib/useSearchAutoComplete';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Item } from '@v2/shared/lib/useAutoComplete';
import { Category } from '@v2/shared/lib/types';

export interface Props {
  categories?: Category[];
}

/**
 * The page behind every 404 the storefront serves.
 *
 * It carries the standard chrome because it declares Layouts.Full like any other page,
 * which is the whole point of the change: a visitor who mistypes a url keeps the
 * navigation, the header search and the footer instead of landing on a dead end. The
 * per-locale differences — logo, footer links, the /au/ prefix on every url — come from
 * the layout and from useSite, so this component says nothing about which storefront it
 * is on.
 *
 * The search is the header's own component and hook, so a visitor who has just hit a
 * wrong url gets suggestions as they type rather than a form that only rewards an exact
 * guess. It needs an input id of its own: the header already renders two of these, one
 * per breakpoint, and a third sharing an id would break the label and the aria wiring.
 *
 * `categories` are the site's featured categories, listed by the page's own props builder
 * rather than taken from the layout's. They can arrive empty — see the builder — and an
 * empty band renders nothing.
 */
export default function NotFound({ categories = [] }: Props) {
  const { buildUrl } = useUrlGenerator();

  const searchAutoCompleteProps = useSearchAutoComplete((item: Item) => {
    // Through buildUrl rather than a bare path: AU serves from /au/, where a plain
    // /search would drop the visitor onto the storefront they were not on.
    window.location.href = item.url || buildUrl(`/search?xSearch=${encodeURIComponent(item.title)}`);
  });

  return (
    // Extra on top of the layout's standard padding, not instead of it: an error page is
    // a short one, and its message reads better set away from the chrome than tight
    // under it. The measure is the layout's, like every other page's.
    <div className="py-5 md:py-10">
      <div className="max-w-2xl mx-auto text-center mb-10 md:mb-16">
        <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">Error 404</p>

        <h1 className="mt-2 font-bold text-3xl md:text-4xl text-slate-900">We can&rsquo;t find that page</h1>

        <p className="mt-4 text-base md:text-lg text-slate-600">
          The page you asked for may have moved, or the address may have a typo in it. Search for a title below, or
          browse one of our categories.
        </p>

        <div className="mt-8 flex justify-center">
          <div className="flex w-full max-w-md group relative">
            <SearchForm id="not-found-search" searchAutoCompleteProps={searchAutoCompleteProps} />
          </div>
        </div>

        <p className="mt-6 text-sm">
          <Link href={buildUrl('/')} className="font-semibold text-slate-600 hover:text-slate-900 underline">
            Go to the home page
          </Link>
        </p>
      </div>

      <CategoryBand title="Popular Categories" categories={categories} />
    </div>
  );
}

NotFound.layoutName = 'Layouts.Full';
